/**
 * SHELL — leaderboard screen (global + friends tabs).
 *
 * Fetches a LeaderboardPage per scope via the network client and renders rows,
 * highlighting the caller's own row. Every transport state is handled: loading,
 * pending (501 — Backend not live yet), offline, unauthorized, error, and the
 * empty board. Friends scope nudges anonymous players to claim an account.
 */

import type { LeaderboardEntry, LeaderboardPage, LeaderboardScope } from '@/types';
import type { App } from '../../app';
import type { Screen } from '../router';
import { div, el, button, replaceChildren } from '../dom';
import { brand, banner, spinner, iconButton } from '../components';
import { formatScore, formatTime, formatDateLabel } from '../format';
import { describeFailure } from '../../net/client';
import { icons } from '../icons';

export function leaderboardScreen(app: App): Screen {
  const snap = app.store.get();
  const screen = div('dr-screen');
  const card = div('dr-card');

  card.append(brand());
  card.append(el('div', { class: 'dr-date', text: formatDateLabel(snap.dateKey) }));

  let scope: LeaderboardScope = 'global';

  // Tabs.
  const globalTab = button('dr-tab', 'Global', () => select('global'));
  const friendsTab = button('dr-tab', 'Friends', () => select('friends'));
  globalTab.setAttribute('aria-selected', 'true');
  friendsTab.setAttribute('aria-selected', 'false');
  const tabs = div('dr-tabs', globalTab, friendsTab);
  tabs.setAttribute('role', 'tablist');
  card.append(tabs);

  const body = div('dr-board');
  body.setAttribute('aria-live', 'polite');
  card.append(body);

  screen.append(card);
  screen.append(iconButton('dr-btn dr-btn--ghost dr-btn--block', 'back', 'Back', () => app.go(snap.dailyStatus === 'completed' ? 'results' : 'landing')));

  let reqToken = 0;

  function select(next: LeaderboardScope): void {
    if (scope === next) return;
    scope = next;
    globalTab.setAttribute('aria-selected', String(scope === 'global'));
    friendsTab.setAttribute('aria-selected', String(scope === 'friends'));
    void load();
  }

  async function load(): Promise<void> {
    const token = ++reqToken;
    replaceChildren(body, spinner('Loading leaderboard…'));

    // Friends requires an account.
    if (scope === 'friends' && snap.identity.kind !== 'claimed') {
      replaceChildren(
        body,
        banner('info', 'Claim your streak to add friends and see how you stack up.'),
        iconButton('dr-btn dr-btn--primary dr-btn--block', 'check', 'Claim my streak', () => app.go('claim')),
      );
      return;
    }

    const res = await app.net.leaderboard(snap.dateKey, scope);
    if (token !== reqToken) return; // a newer request superseded this one

    if (!res.ok) {
      const kind = res.failure.kind === 'pending' || res.failure.kind === 'offline' ? 'info' : res.failure.kind === 'unauthorized' ? 'warn' : 'err';
      replaceChildren(
        body,
        banner(kind, describeFailure(res.failure)),
        iconButton('dr-btn dr-btn--ghost dr-btn--block', 'refresh', 'Try again', () => void load()),
      );
      return;
    }

    renderPage(res.data);
  }

  function renderPage(page: LeaderboardPage): void {
    if (page.entries.length === 0) {
      replaceChildren(body, banner('info', 'No scores yet today — be the first to set the pace!'));
      return;
    }
    const rows = page.entries.map((e) => row(e, e.userId === snap.identity.userId));
    replaceChildren(body, ...rows);

    // Pin the caller's own row at the bottom if it isn't already in the page.
    if (page.me && !page.entries.some((e) => e.userId === page.me!.userId)) {
      body.append(el('hr', { class: 'dr-divider' }), row(page.me, true));
    }
    body.append(el('div', { class: 'dr-faint', text: `${page.total.toLocaleString('en-US')} players today` }));
  }

  void load();

  return { node: screen };
}

function row(e: LeaderboardEntry, isMe: boolean): HTMLElement {
  const rankClass = e.rank <= 3 ? `dr-row__rank dr-row__rank--${e.rank}` : 'dr-row__rank';
  const node = div(isMe ? 'dr-row dr-row--me' : 'dr-row');
  const sub = e.streak > 1
    ? el('span', { class: 'dr-row__sub', html: `${icons.flame} ${e.streak}` })
    : el('span', { class: 'dr-row__sub', text: formatTime(e.finishTimeMs) });
  const nameCol = div('');
  nameCol.append(el('div', { class: 'dr-row__name', text: isMe ? `${e.displayName} (you)` : e.displayName }), sub);
  node.append(
    el('div', { class: rankClass, text: String(e.rank) }),
    nameCol,
    el('div', { class: 'dr-row__score', text: formatScore(e.score) }),
  );
  return node;
}
